/**
 * Lock distribuído via localStorage (TTL + fencing + verify-after-write).
 */

import { getTabId as getCoordinatorTabId } from '../tabCoordinator';

const LOCK_KEY = 'GOMARKE_FLUSH_LOCK';
import { FLUSH_LOCK_TTL_MS } from './coordinationConstants';

const DEFAULT_TTL_MS = FLUSH_LOCK_TTL_MS;

export interface FlushLock {
  holderTabId: string;
  expiresAt: number;
  fencingToken: number;
  epoch: number;
}

function getTabId(): string {
  if (typeof window === 'undefined') return 'server';
  const coordinatorId = getCoordinatorTabId();
  if (coordinatorId) return coordinatorId;
  const w = window as unknown as { __gomarkeTabId?: string };
  if (!w.__gomarkeTabId) {
    w.__gomarkeTabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }
  return w.__gomarkeTabId;
}

function readLock(): FlushLock | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LOCK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FlushLock;
    return {
      ...parsed,
      epoch: parsed.epoch ?? 0,
    };
  } catch {
    return null;
  }
}

function writeLock(lock: FlushLock): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCK_KEY, JSON.stringify(lock));
}

function isLockHeldByOther(lock: FlushLock | null, now: number): boolean {
  if (!lock) return false;
  return lock.expiresAt > now && lock.holderTabId !== getTabId();
}

function verifyOwnership(expected: FlushLock): boolean {
  const current = readLock();
  if (!current) return false;
  return (
    current.holderTabId === expected.holderTabId &&
    current.fencingToken === expected.fencingToken &&
    current.epoch === expected.epoch
  );
}

export function isLockStale(lock: FlushLock | null, now = Date.now()): boolean {
  if (!lock) return true;
  return lock.expiresAt <= now;
}

export function tryAcquireFlushLock(ttlMs = DEFAULT_TTL_MS): boolean {
  const now = Date.now();
  const existing = readLock();
  if (isLockHeldByOther(existing, now)) {
    return false;
  }
  const token = (existing?.fencingToken ?? 0) + 1;
  const epoch = (existing?.epoch ?? 0) + (isLockStale(existing, now) ? 1 : 0);
  const candidate: FlushLock = {
    holderTabId: getTabId(),
    expiresAt: now + ttlMs,
    fencingToken: token,
    epoch,
  };
  writeLock(candidate);
  if (!verifyOwnership(candidate)) {
    return false;
  }
  return true;
}

export function renewFlushLock(ttlMs = DEFAULT_TTL_MS): boolean {
  const lock = readLock();
  if (!lock || lock.holderTabId !== getTabId()) return false;
  const renewed: FlushLock = {
    ...lock,
    expiresAt: Date.now() + ttlMs,
  };
  writeLock(renewed);
  return verifyOwnership(renewed);
}

export function releaseFlushLock(): void {
  const lock = readLock();
  if (lock?.holderTabId === getTabId()) {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(LOCK_KEY);
    }
  }
}

export function hasValidFlushLock(): boolean {
  const lock = readLock();
  if (!lock) return false;
  return (
    lock.expiresAt > Date.now() &&
    lock.holderTabId === getTabId() &&
    verifyOwnership(lock)
  );
}

export function getFlushLockFencingToken(): number {
  return readLock()?.fencingToken ?? 0;
}

export function getFlushLockEpoch(): number {
  return readLock()?.epoch ?? 0;
}

export function validateFencingToken(minToken: number): boolean {
  const lock = readLock();
  if (!lock || lock.holderTabId !== getTabId()) return false;
  return lock.fencingToken >= minToken;
}

export function getFlushLockHolder(): string | null {
  return readLock()?.holderTabId ?? null;
}

export function _testClearFlushLock(): void {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(LOCK_KEY);
  }
}
